import * as React from "react";
import { Dialog, Tabs } from "@/components/ui";
import { useDynamicPromptsAccess } from "@/utils/useDynamicPromptsAccess";
import { BriefAction, BriefPicks, defaultPicks } from "./BriefBuilder";
import { GUIDE_LINKS } from "./guideLinks";
import { HelpDialogProps, HelpFooter, HelpTopic } from "./HelpDialog";
import { HowBackgroundsWorkGuide } from "./HowBackgroundsWorkGuide";
import { HowItWorksGuide } from "./HowItWorksGuide";
import { WhatCanIAskGuide } from "./WhatCanIAskGuide";

interface Guide {
  topic: HelpTopic;
  /** Short enough for three tabs on one line */
  tab: string;
  title: string;
  /** The guide in a sentence or two, read before anything else on the tab */
  summary: string;
  /** Replaces `summary` when the dialog is opened while refining a design */
  refineSummary?: string;
}

const GUIDES: Guide[] = [
  {
    topic: "how",
    tab: "How it works",
    title: "How Design Studio works",
    summary:
      "You design and approve a post once. Every player who shares it gets a personalized copy, built from exactly what you approved.",
  },
  {
    topic: "ask",
    tab: "What to ask for",
    title: "What can I ask for?",
    summary:
      "Describe the look: layout, background, mood, and type. The words, player details, and branding fill in on their own.",
    refineSummary:
      "Ask for changes in plain words, one at a time. The words, player details, and branding fill in on their own.",
  },
  {
    topic: "backgrounds",
    tab: "Backgrounds",
    title: "How backgrounds work",
    summary:
      "Every design starts with a ready photo from your library. It's the one part players can change, and you decide how much.",
  },
];

const GuideHeader: React.FC<{ guide: Guide; refining: boolean }> = ({ guide, refining }) => (
  <div className="space-y-1">
    <h3 className="text-lg font-bold text-slate-900">{guide.title}</h3>
    <p className="text-sm leading-normal text-slate-500">{(refining && guide.refineSummary) || guide.summary}</p>
  </div>
);

interface DesignStudioHelpDialogProps extends HelpDialogProps {
  /** The guide the dialog opens on, each time it opens. */
  topic?: HelpTopic;
  /** Where the brief builder starts, each time it opens; the access-based defaults when omitted. */
  picks?: BriefPicks;
  /** What the brief builder's button does; opens New design by default. */
  briefAction?: BriefAction;
}

const START: BriefAction = { kind: "start" };

type HelpBodyProps = Required<Pick<DesignStudioHelpDialogProps, "topic" | "briefAction">> &
  Pick<DesignStudioHelpDialogProps, "picks"> & { onClose(): void };

// Mounted only while the dialog is open, so each open starts fresh on `topic` and `picks`, and access is
// only looked up when someone reads the help.
const HelpBody: React.FC<HelpBodyProps> = ({ topic, picks: initialPicks, briefAction, onClose }) => {
  const { canCreateReviewDesigns } = useDynamicPromptsAccess();
  const [tab, setTab] = React.useState<HelpTopic>(topic);
  // Held here rather than in the builder so picks survive a trip to another tab
  const [picks, setPicks] = React.useState<BriefPicks>(() => initialPicks ?? defaultPicks(canCreateReviewDesigns));

  const contentRef = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    contentRef.current?.scrollTo?.({ top: 0 });
  }, [tab]);

  return (
    <Tabs value={tab} onValueChange={value => setTab(value as HelpTopic)} className="gap-0 min-w-0">
      <Dialog.Header className="pb-0 border-b-0">
        <Dialog.Title className="text-xl font-bold font-sans">Help</Dialog.Title>
        <Tabs.List variant="menu" className="overflow-x-auto">
          {GUIDES.map(g => (
            <Tabs.Trigger key={g.topic} value={g.topic}>
              {g.tab}
            </Tabs.Trigger>
          ))}
        </Tabs.List>
      </Dialog.Header>

      <Dialog.Content ref={contentRef} className="max-h-[70vh] overflow-y-auto py-4">
        {GUIDES.map(g => (
          <Tabs.Content key={g.topic} value={g.topic} className="space-y-6">
            <GuideHeader guide={g} refining={briefAction.kind === "refine"} />
            {g.topic === "how" && <HowItWorksGuide onNavigate={setTab} />}
            {g.topic === "ask" && (
              <WhatCanIAskGuide
                onNavigate={setTab}
                builder={{ picks, onChange: setPicks, action: briefAction, onDone: onClose }}
              />
            )}
            {g.topic === "backgrounds" && <HowBackgroundsWorkGuide />}
          </Tabs.Content>
        ))}
      </Dialog.Content>

      <Dialog.Footer>
        <HelpFooter guideUrl={GUIDE_LINKS[tab]} onClose={onClose} />
      </Dialog.Footer>
    </Tabs>
  );
};

/** The three Design Studio guides as tabs; a link from one guide to another switches tabs. */
export const DesignStudioHelpDialog: React.FC<DesignStudioHelpDialogProps> = ({
  open,
  onOpenChange,
  topic = "how",
  picks,
  briefAction = START,
}) => (
  <Dialog open={open} onOpenChange={next => onOpenChange(next)} size="xl" showCloseButton={false}>
    <HelpBody topic={topic} picks={picks} briefAction={briefAction} onClose={() => onOpenChange(false)} />
  </Dialog>
);
